import React, { useCallback, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { useProblemStore } from '../context/ProblemStoreProvider';
import { newProblemTemplate } from '../data/problemTemplate';
import type { Problem } from '../data/problems';
import './ProblemEditorDrawer.css';

// Admin-only raw editor for a problem's full JSON body. This single Monaco-bound
// surface covers the entire schema (description, examples, hints, multi-file
// `files`, testCases, per-language referenceSolution) for every problem type, so
// there's one robust authoring path rather than a bespoke control per field.
// Save funnels through the problem store (POST for a new id, PUT to replace).

const KNOWN_TYPES = ['algorithm', 'class', 'fullstack', 'agent', 'react'];
const REQUIRED = ['id', 'realmId', 'skillId', 'title', 'difficulty', 'type', 'description'];

interface Props {
  /** Problem to edit; `null` opens the New-problem template. */
  initial: Problem | null;
  onClose: () => void;
  /** Called with the saved body after a successful create/replace. */
  onSaved: (problem: Problem) => void;
}

// Mirror the backend's cheap structural check so an author gets instant feedback
// before the round-trip (the server validates again authoritatively).
function validate(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return 'Body must be a JSON object.';
  const b = body as Record<string, unknown>;
  const missing = REQUIRED.filter(f => !b[f]);
  if (missing.length) return `Missing required field(s): ${missing.join(', ')}`;
  if (!KNOWN_TYPES.includes(b.type as string)) return `Unknown type: ${String(b.type)}`;
  return null;
}

const ProblemEditorDrawer: React.FC<Props> = ({ initial, onClose, onSaved }) => {
  const { saveProblem } = useProblemStore();
  const isNew = initial === null;
  const initialText = useMemo(
    () => JSON.stringify(initial ?? newProblemTemplate(), null, 2),
    [initial],
  );
  const [text, setText] = useState(initialText);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(`Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`);
      return;
    }
    const problem = validate(parsed);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveProblem(parsed as Problem);
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [text, saveProblem, onSaved]);

  return (
    <div className="ped-overlay" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="ped-drawer" onMouseDown={e => e.stopPropagation()}>
        <div className="ped-head">
          <span className="ped-title">{isNew ? 'New problem' : `Edit ${initial?.id}`}</span>
          <button className="ped-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="ped-editor">
          <Editor
            height="100%"
            defaultLanguage="json"
            value={text}
            onChange={v => setText(v ?? '')}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              tabSize: 2,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </div>
        {error && <div className="ped-error">{error}</div>}
        <div className="ped-actions">
          <button className="ped-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ped-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create problem' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProblemEditorDrawer;
